/**
 * ステータスバー（§0.3.2「一目で分かること」・U159）。
 * 表示範囲・選択長・GC・位相・注釈件数・常駐チャンクを常時出す。
 */

import { useTranslation } from 'react-i18next';
import {
  formatIntervalRange1Based,
  type ChunkedSequence,
  type Interval,
  type ViewRange,
} from '@kensuke/genome-core';
import type { StoredSequenceMeta } from '../../../services/geneAnalysisChunkStore.service';

export interface GeneAnalysisStatusBarProps {
  meta: StoredSequenceMeta;
  sequence: ChunkedSequence;
  view: ViewRange;
  selection: Interval | null;
  featureCount: number;
  /** 塩基ビューが出ているか。`view` は実表示範囲なので縮尺からは判定しない */
  baseMode: boolean;
}

function formatBases(count: number): string {
  return `${Math.round(count).toLocaleString()} bp`;
}

export default function GeneAnalysisStatusBar({
  meta,
  sequence,
  view,
  selection,
  featureCount,
  baseMode,
}: GeneAnalysisStatusBarProps) {
  const { t } = useTranslation();
  const gc = selection ? sequence.gcPercentSync(selection) : null;

  return (
    <div className="ga-status-bar" data-testid="gene-analysis-status-bar">
      <span className="ga-status-item ga-status-name" title={meta.description || meta.name}>
        {meta.name}
      </span>
      <span className="ga-status-item">{formatBases(meta.length)}</span>
      <span className="ga-status-item">
        {meta.topology === 'circular' ? t('geneAnalysis.status.circular') : t('geneAnalysis.status.linear')}
      </span>
      <span className="ga-status-item">
        {t('geneAnalysis.status.view')}: {formatIntervalRange1Based({ start: Math.floor(view.start), end: Math.ceil(view.end) }, meta.length)}
      </span>
      <span className="ga-status-item">
        {t('geneAnalysis.status.selection')}:{' '}
        {selection && selection.end > selection.start
          ? `${formatIntervalRange1Based(selection, meta.length)} · ${formatBases(
              selection.end - selection.start
            )}${gc === null ? '' : ` · GC ${gc.toFixed(1)}%`}`
          : t('geneAnalysis.status.none')}
      </span>
      <span className="ga-status-item">
        {t('geneAnalysis.status.features')}: {featureCount.toLocaleString()}
      </span>
      <span className="ga-status-item ga-status-mode">
        {baseMode ? t('geneAnalysis.status.mode_bases') : t('geneAnalysis.status.mode_map')}
      </span>
      <span className="ga-status-item ga-status-resident">
        {t('geneAnalysis.status.resident')}: {(sequence.residentByteCount / (1024 * 1024)).toFixed(1)} MB
      </span>
    </div>
  );
}
